import { useSelector, useDispatch } from 'react-redux';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useTitle } from "../../../../../config/global";
import "../../../css/setup.css";

import AddContentPopup from '../../../../SharedComponents/AddContentPopup/AddContentPopup';
import DragDrop from '../../../../SharedComponents/DragDrop/DragDrop';
import { usePlaylistFoldState } from '../../../../SharedComponents/DragDrop/usePlaylistFoldState';
import { MediaPreviewProvider, useMediaPreview, isPreviewable } from '../../../../SharedComponents/MediaPreview/MediaPreviewContext';

import { Skeleton } from 'primereact/skeleton';
import { Button } from '../../../../../components/ui/Button';

import { useRetrieveNewMediaDuration, useUpdateCampaignLayerSettings } from '../../../../../queries';
import { setAddContentSource, setDialog, setLayer, setLayerSettingsTab, toggleDialog } from '../../../../../redux/Slices/Global/globalSlice';
import FirebaseMediaListener from '../../../../Services/FirebaseMediaListener';
import BuilderLayerSettingsSheet from './settings/BuilderLayerSettingsSheet';
import CampaignVibesSection from './CampaignVibesSection';

/**
 * Layer-3 campaign builder — main content ("المحتوى الرئيسي") + الصور
 * (أجواء). The layer dropdown (MAIN/PLAYGROUND/CORNER/BANNER) is gone: this
 * page only ever shows/edits the MAIN layer now. PLAYGROUND/CORNER/BANNER
 * data on existing campaigns is untouched — the player is data-driven per
 * layer and keeps rendering it unchanged (verified zero-diff).
 */
// v15 media preview — the provider is mounted here (this page root), wrapping
// the whole MAIN-content + vibes tree, so one audio engine / lightbox is
// shared by every reuse point on this page (MAIN rows via CampaignCard,
// vibes rows/sub-items via CampaignVibesSection, and the add-content sheet's
// media/YouTube tabs via AddContentPopup -> MediaList/YouTubeSearchList).
// Unmounting this page (route change) tears the provider down with it,
// which is the "stop audio/video on route change" behavior the task asks
// for — see MediaPreviewContext.jsx's own doc comment.
function CampaignContentSetup(props) {
    return (
        <MediaPreviewProvider>
            <CampaignContentSetupBody {...props} />
        </MediaPreviewProvider>
    );
}

function CampaignContentSetupBody({ loading, data, refetch, isError }) {
    useTitle("Campaign Setup")
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const { openDialog, addContentSource, campaignDetails } = useSelector((state) => state.global);

    const mediaIdResetRef = useRef([]);
    const retrieveNewMediaDuration = useRetrieveNewMediaDuration()
    const updateLayerSettings = useUpdateCampaignLayerSettings();

    // F2: unified-settings visibility for المحتوى الرئيسي.
    const mainLayerPolicy = campaignDetails?.settings?.live?.layer_settings?.MAIN || {};
    const mainUnifyActive = !!mainLayerPolicy.apply_layer_settings;
    const mainHasVideo = (data || []).some((x) => String(x.type).toUpperCase() === 'VIDEO');

    // Spec item D: fold-all button + per-viewer persisted fold state, only
    // relevant when the MAIN layer has at least one playlist row.
    const mainPlaylistIds = useMemo(
        () => (data || []).filter((row) => row.playlist_id != null).map((row) => row.playlist_id),
        [data]
    );
    const mainFold = usePlaylistFoldState(campaignDetails.id, 'MAIN', mainPlaylistIds);

    const openLayerSheet = (tab = 'display') => {
        dispatch(setLayer('MAIN'));
        dispatch(setLayerSettingsTab(tab));
        dispatch(toggleDialog('layerSettings'));
    };
    // "إيقاف التوحيد" — same save endpoint the sheet itself uses, fired
    // without opening it. useUpdateCampaignLayerSettings's own onSuccess
    // unconditionally TOGGLES openDialog.layerSettings (true<->false, meant
    // for the sheet's own save button) — since the sheet starts CLOSED here,
    // that flip would pop it open right after this "quick disable" succeeds.
    // Forcing it back closed after the awaited mutation resolves (which
    // react-query runs the hook's own onSuccess before) cancels that out.
    const disableMainUnify = async () => {
        await updateLayerSettings.mutateAsync({ id: campaignDetails.id, layer: 'MAIN', layer_settings: { apply_layer_settings: false } });
        dispatch(setDialog({ key: 'layerSettings', value: false }));
    };

    const galleryRefetch = useCallback(async () => {
        const currentMediaIds = mediaIdResetRef?.current;
        if (currentMediaIds?.length > 0) {
            for (const element of currentMediaIds) {
                await retrieveNewMediaDuration.mutateAsync({ id: element });
            }
        }
    }, []);

    // The .content-wrapper/.scroll_div containment class is now owned by
    // CampaignSetup.jsx (the shell that renders the pinned appbar).

    // v15 media preview — row-thumbnail clicks (DragDrop.jsx's onRowPreview)
    // call this with (item, list); the lightbox's own nav scope is that
    // list's visual (image/video/audio) siblings only (task spec: "every
    // visual... sibling in that SAME row list"), and row previews stop any
    // playing audio on close (stopAudioOnClose), unlike the sheet-grid's own
    // preview. Replaces this page's previous single-item PreviewMediaDialog
    // (TV-frame) row preview — that dialog is untouched and still used
    // elsewhere (Media Gallery etc.), just no longer rendered from here.
    const mediaPreview = useMediaPreview();
    const openRowPreview = (item, list) => {
        const visual = (list || []).filter(isPreviewable);
        const idx = visual.indexOf(item);
        mediaPreview.openLightbox(visual, idx < 0 ? 0 : idx, { stopAudioOnClose: true });
    };

    const openAddContent = () => {
        // Explicit for the same reason as the layer-settings gear above:
        // the الصور (أجواء) section can leave redux `layer` on PLAYGROUND.
        dispatch(setLayer('MAIN'));
        dispatch(setAddContentSource('main'));
        dispatch(toggleDialog('addContent'));
    };

    return (
        <>
            <section className="pp-cb-section">
                <div className="pp-cb-sec-head">
                    <div className="pp-cb-txt">
                        <h2>
                            {t('show.show_main_content_title')}
                            {mainUnifyActive && <span className="tag">{t('show.show_unified_badge')}</span>}
                        </h2>
                        <div className="pp-cb-desc">{t('show.show_main_content_desc')}</div>
                    </div>
                    {mainPlaylistIds.length > 0 && (
                        <button
                            type="button"
                            className="pp-cb-foldall"
                            onClick={() => mainFold.setAll(!mainFold.anyOpen)}
                            title={mainFold.anyOpen ? t('dragdrop.fold_all_playlists') : t('dragdrop.unfold_all_playlists')}
                        >
                            <i className={`pi ${mainFold.anyOpen ? 'pi-angle-double-up' : 'pi-angle-double-down'}`} />
                            <span className="fl">{mainFold.anyOpen ? t('dragdrop.fold_all_playlists') : t('dragdrop.unfold_all_playlists')}</span>
                        </button>
                    )}
                    <button
                        className="pp-cb-icbtn"
                        title={t('show.show_layer_settings')}
                        aria-label={t('show.show_layer_settings')}
                        // Explicit, not assumed: the الصور (أجواء) section
                        // below briefly flips this same redux `layer` to
                        // PLAYGROUND for its own edit/attach flow, so this
                        // MAIN-only dialog must claim it back at open time
                        // rather than trust it was already MAIN.
                        onClick={() => openLayerSheet('display')}
                    >
                        <i className="pi pi-cog" />
                    </button>
                </div>

                {mainUnifyActive && (
                    <div className="pp-cb-warn uni">
                        <div className="wt">
                            <i className="pi pi-exclamation-triangle" />
                            <span>
                                {t('show.show_unify_banner_text')}
                                {mainHasVideo ? ` ${t('show.show_unify_banner_video_note')}` : ''}
                            </span>
                        </div>
                        <div className="wa">
                            <Button outlined label={t('show.show_unify_disable')} onClick={disableMainUnify} loading={updateLayerSettings.isLoading} />
                            <Button text label={t('show.show_unify_open')} onClick={() => openLayerSheet('display')} />
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="pp-cb-list">
                        {[0, 1, 2].map((i) => (
                            <div className="pp-cb-sk" key={i}>
                                <Skeleton shape="square" size="46px" borderRadius="11px" />
                                <div style={{ flex: 1 }}>
                                    <Skeleton width="55%" height="13px" />
                                    <Skeleton width="32%" height="11px" style={{ marginTop: 8 }} />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : isError ? (
                    <div className="pp-blank pp-cb-blank pp-blank--err">
                        <div className="pp-blank-icon"><i className="pi pi-exclamation-circle" /></div>
                        <h3>{t('show.show_main_content_error_title')}</h3>
                        <p>{t('show.show_main_content_error_desc')}</p>
                        <div className="pp-blank-actions">
                            <Button label={t('buttons.retry')} icon="pi pi-refresh" onClick={() => refetch?.()} />
                        </div>
                    </div>
                ) : data?.length === 0 ? (
                    <div className="pp-blank">
                        <div className="pp-blank-icon"><i className="bx bxs-playlist" /></div>
                        <h3>{t('show.show_no_media_yet')}</h3>
                        <p>{t('show.show_main_layer_empty_note')}</p>
                        <div className="pp-blank-actions">
                            <Button label={t('show.show_add_content')} icon="pi pi-plus" onClick={openAddContent} />
                        </div>
                    </div>
                ) : (
                    <>
                        <DragDrop
                            data={data}
                            onPreview={openRowPreview}
                            builderRow
                            isPlaylistOpen={mainFold.isOpen}
                            onTogglePlaylistOpen={mainFold.toggle}
                        />
                        <button className="pp-cb-additem" onClick={openAddContent}>
                            <i className="pi pi-plus" />
                            {t('show.show_add_content')}
                        </button>
                    </>
                )}
            </section>

            <CampaignVibesSection />

            {openDialog.addContent && addContentSource === 'main' && <AddContentPopup loading={loading} simplifiedMainMode />}
            {openDialog.addContent && addContentSource === 'vibes' && (
                <AddContentPopup
                    restrictToPlaylistOnly
                    titleOverride={t('addcontent.title_vibes')}
                    subtitleOverride={t('addcontent.subtitle_vibes')}
                />
            )}
            {openDialog.layerSettings ? <BuilderLayerSettingsSheet layerData={data} /> : <></>}

            <FirebaseMediaListener
                path={"/Companies/" + localStorage.getItem("company_id") + "/actions"}
                getData={galleryRefetch}
            />
        </>
    )
}

export default CampaignContentSetup
